import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import type {ProfileCluster} from "crqlar-react-common/dist/models/profile/profileCluster.model";
import {ProfileClusterService} from "crqlar-react-common/dist/services/profile/profileCluster.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {ProfileClusterActionChip, ProfileClusterTypeChip} from "./ProfileClusterStatusChip";
import SettingsListPage from "../layout/SettingsListPage";

const ProfileClustersList = () => {
    useTrackScreenVisit('system profile clusters');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<ProfileCluster>[]>(() => [
        {
            id: 'clusterId',
            header: t('Cluster ID'),
            accessorFn: cluster => cluster.clusterId,
            cell: ({row}) => <Box sx={{whiteSpace: 'nowrap'}}>{row.original.clusterId || ''}</Box>,
            size: 300,
            minSize: 300,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'clusterType',
            header: t('Type'),
            accessorFn: cluster => cluster.clusterType,
            cell: ({row}) => <ProfileClusterTypeChip type={row.original.clusterType}/>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'size',
            header: t('Size'),
            accessorFn: cluster => cluster.size,
            cell: ({row}) => <Box>{row.original.size ?? ''}</Box>,
            size: 100,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'coherenceScore',
            header: t('Coherence Score'),
            accessorFn: cluster => cluster.coherenceScore,
            cell: ({row}) => <Box>{row.original.coherenceScore?.toFixed(2) ?? ''}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'suggestedAction',
            header: t('Suggested Action'),
            accessorFn: cluster => cluster.suggestedAction,
            cell: ({row}) => <ProfileClusterActionChip action={row.original.suggestedAction}/>,
            size: 220,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'formationReason',
            header: t('Formation Reason'),
            accessorFn: cluster => cluster.formationReason,
            cell: ({row}) => <Box>{row.original.formationReason || ''}</Box>,
            size: 320,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: cluster => cluster.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: cluster => cluster.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-profile-clusters',
        columns,
        getRowId: cluster => cluster.id!,
        onRowClick: cluster => navigate(`/system/profileClusters/${cluster.id}`),
        initialSorting: [{id: 'coherenceScore', desc: true}],
        queryFn: async ({pagination, sorting}) => {
            const page = await ProfileClusterService.getAllProfileClusters({
                minSize: 2,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <SettingsListPage title={t('Profile clusters')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ProfileClustersList;
